import { Injectable, OnDestroy } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { TwcPopupService } from '@topcon/twc-angular';

import { TranslationService } from '@mirage-projects/cw-commons';
import { PcvizExpressionObject } from '@mirage-projects/cw-models';
import { GisPcvizService } from '@mirage-projects/cw-pcviz-core';

import { ExpressionConfig, FieldExpressionItem, Operator } from './expression-config';
import { ExpressionEditorComponent } from './expression-editor.component';

type ExpressionData = {
    expressionString: string;
    fieldsExpression?: FieldExpressionItem[];
    expressionState?: ExpressionConfig;
};
/**
 * Service to control the sidebar drawer expression editor.
 */
@Injectable({ providedIn: 'root' })
export class ExpressionEditorService implements OnDestroy {
    private readonly inputExpressionSubject$: Subject<ExpressionData> = new Subject<ExpressionData>();
    inputExpressionChange$ = this.inputExpressionSubject$.asObservable();

    private readonly outputExpressionSubject$: Subject<ExpressionData> = new Subject<ExpressionData>();
    outputExpressionChange$ = this.outputExpressionSubject$.asObservable();

    expressionString = '';
    fieldsExpression?: FieldExpressionItem[] = [];
    expressionState!: ExpressionConfig;

    private readonly onDestroy$ = new Subject<void>();

    static fieldArraysToFieldExpression(fieldIds: string[], fieldTypes: string[]): FieldExpressionItem[] {
        return fieldIds.map((id, index) => ({ id, type: fieldTypes[index] ?? null }));
    }

    constructor(
        private gisPcvizService: GisPcvizService,
        private translationService: TranslationService,
        private popupService: TwcPopupService
    ) {
        this.inputExpressionChangeSubscriber();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    /**
     * Sets the expression editor state object
     * @param expressionState Expression editor state object
     */
    setExpressionState(expressionState: ExpressionConfig): void {
        this.expressionState = expressionState;
        this.fieldsExpression = expressionState.fieldsExpression;
    }

    /**
     * Initializes the subscription to the input expression change observable.
     */
    inputExpressionChangeSubscriber(): void {
        this.inputExpressionChange$.pipe(takeUntil(this.onDestroy$)).subscribe((expressionData) => {
            this.expressionState.customExpr = expressionData.expressionString;
            this.expressionState.fieldsExpression = expressionData.fieldsExpression ?? [];

            this.setOptionsFromExprString(expressionData.expressionString);
        });
    }

    /**
     * Shows the expression editor.
     */
    showExpressionEditor(): void {
        this.popupService.open(ExpressionEditorComponent);
    }

    /**
     * Sets the input expression string and triggers change
     * @param expressionString The string representation of the expression.
     */
    setInputExpressionString(expressionString: string): void {
        this.expressionString = expressionString;
        this.inputExpressionSubject$.next({
            expressionString,
            fieldsExpression: this.fieldsExpression,
        });
    }

    /**
     * Sets the output expression string and triggers change
     * @param expressionString The string representation of the expression.
     */
    setOutputExpressionString(expressionString: string): void {
        this.expressionString = expressionString;
        this.outputExpressionSubject$.next({
            expressionString,
            fieldsExpression: this.fieldsExpression,
            expressionState: this.expressionState,
        });
    }

    /**
     * Checks if the field expression is of predefined type.
     */
    isPredefined(): boolean {
        return this.expressionState.exprType === 'EXPRESSION_EDITOR.PREDEFINED';
    }

    /**
     * Checks if the field expression is of custom type.
     */
    isCustom(): boolean {
        return this.expressionState.exprType === 'EXPRESSION_EDITOR.CUSTOM';
    }

    /**
     * Sets the expression type to predefined and triggers change.
     */
    setPredefined(): void {
        this.expressionState.exprType = 'EXPRESSION_EDITOR.PREDEFINED';
        this.changePredefinedExpr();
    }

    /**
     * Sets the expression type to custom and triggers change.
     */
    setCustom(): void {
        this.expressionState.exprType = 'EXPRESSION_EDITOR.CUSTOM';
        this.applyExpression(this.expressionState.customExpr);
    }

    /**
     * Translates the field name to a string.
     */
    translateByField(field: string): string {
        const defaultKey = field;
        if (!defaultKey) {
            return '';
        }
        const key = `EXPRESSION_EDITOR.${this.convertToConst(defaultKey)}`;

        return this.translationService.translateWithDefault(key, defaultKey);
    }

    /**
     * Gets the list of available operators for current field.
     */
    getVisibleOperators(): Operator[] {
        const operators = this.expressionState.operators.filter((operator) => {
            if (!this.expressionState.field) {
                return false;
            }

            const fieldIndex = this.hasStateFieldValidIndex();
            if (fieldIndex === false) {
                return false;
            }

            if (this.expressionState.fieldsExpression[fieldIndex]?.type !== 'BooleanField') {
                return true;
            }

            return operator.useWithBool;
        });
        return operators;
    }

    /**
     * Get operator tooltip by symbol.
     * @param opSymbol Operator symbol.
     */
    getTip(opSymbol: string): string {
        const operator = this.expressionState.operators.find((op) => op.symbol === opSymbol);

        return operator?.tip ?? '';
    }

    /**
     * Get the input pattern for the current field type.
     */
    getInputPattern(): string {
        if (!this.expressionState.field) {
            return '';
        }

        const fieldIndex = this.hasStateFieldValidIndex();
        if (fieldIndex === false) {
            return '';
        }

        const patterns: Record<string, string> = {
            IntegerField: '-?\\d+',
            FloatField: '-?\\d+(\\.\\d+)?',
            default: '[\\s\\S]*',
        };

        const fieldType = this.expressionState.fieldsExpression[fieldIndex].type;

        return patterns[fieldType] ?? patterns['default'];
    }

    private getFieldIndexById(id: string): number {
        return this.expressionState.fieldsExpression.findIndex((field) => field.id === id);
    }

    private isFieldInExpression(id: string): boolean {
        return this.expressionState.fieldsExpression.some((field) => field.id === id);
    }

    private stringToExpression(exprStr: string): PcvizExpressionObject {
        return this.gisPcvizService.getExpression().stringToExpression(exprStr);
    }

    private setOptionsFromExprString(exprStr: string): void {
        if (exprStr === 'true') {
            this.expressionState.exprType = 'EXPRESSION_EDITOR.ALWAYS_TRUE';
            return;
        }

        this.expressionState.exprType = 'EXPRESSION_EDITOR.CUSTOM';

        const parsedExpr = this.stringToExpression(exprStr);
        if (!parsedExpr) {
            return;
        }

        if (this.isSimpleExpression(parsedExpr)) {
            this.setSimpleExpression(parsedExpr);
            return;
        }

        if (this.isAndExpression(parsedExpr)) {
            this.setAndExpression(parsedExpr);
        }
    }

    private hasStateFieldValidIndex(): false | number {
        const fieldIndex = this.getFieldIndexById(this.expressionState.field);
        const notFound = fieldIndex === -1;
        const badType = !this.expressionState.fieldsExpression[fieldIndex]?.type;
        const badId = !this.expressionState.fieldsExpression[fieldIndex]?.id;

        if (notFound || badType || badId) {
            return false;
        }
        return fieldIndex;
    }

    private applyExpression(exprStr: string): void {
        const parsedExpr = this.stringToExpression(exprStr);
        this.expressionState.isValid = !!parsedExpr;

        if (!this.expressionState.isValid) {
            return;
        }

        this.setOutputExpressionString(exprStr);
    }

    private changePredefinedExpr(): void {
        const exprStr = `[${this.expressionState.field}] ${this.expressionState.op1} ${this.expressionState.value1}`;
        const andExprStr = this.expressionState.useAnd
            ? ` & [${this.expressionState.field}] ${this.expressionState.op2} ${this.expressionState.value2}`
            : '';

        this.expressionState.customExpr = `${exprStr}${andExprStr}`;
        this.applyExpression(this.expressionState.customExpr);
    }

    private convertToConst(name: string): string {
        if (!name) {
            return '';
        }
        return name.replace(/([A-Z])/g, '_$1').toUpperCase();
    }

    private isSimpleExpression(parsedExpr: PcvizExpressionObject): boolean {
        return !!(
            parsedExpr.aritmOp &&
            parsedExpr.leftExp &&
            parsedExpr.rightExp &&
            parsedExpr.leftExp.field &&
            this.isFieldInExpression(parsedExpr.leftExp.field) &&
            parsedExpr.rightExp.constant !== undefined
        );
    }

    private setSimpleExpression(parsedExpr: PcvizExpressionObject): void {
        this.expressionState.exprType = 'EXPRESSION_EDITOR.PREDEFINED';
        this.expressionState.useAnd = false;
        this.expressionState.field = parsedExpr?.leftExp?.field ?? '';
        this.expressionState.op1 = parsedExpr?.aritmOp ?? '';
        this.expressionState.value1 = parsedExpr?.rightExp?.constant ?? '';
    }

    private isAndExpression(parsedExpr: PcvizExpressionObject): boolean {
        const parsedExpL = parsedExpr.leftExp;
        const parsedExpR = parsedExpr.rightExp;

        return !!(
            parsedExpr.logicOp === '&' &&
            parsedExpr.leftExp &&
            parsedExpr.rightExp &&
            parsedExpL?.aritmOp &&
            parsedExpL.leftExp &&
            parsedExpL.rightExp &&
            parsedExpL.leftExp.field &&
            this.isFieldInExpression(parsedExpL.leftExp.field) &&
            parsedExpL.rightExp.constant !== undefined &&
            parsedExpR?.aritmOp &&
            parsedExpR.leftExp &&
            parsedExpR.rightExp &&
            parsedExpR.leftExp.field &&
            parsedExpL.leftExp.field === parsedExpR.leftExp.field &&
            parsedExpR.rightExp.constant !== undefined
        );
    }

    private setAndExpression(parsedExpr: PcvizExpressionObject): void {
        const parsedExpL = parsedExpr.leftExp;
        const parsedExpR = parsedExpr.rightExp;

        this.expressionState.exprType = 'EXPRESSION_EDITOR.PREDEFINED';
        this.expressionState.useAnd = true;
        this.expressionState.field = parsedExpL?.leftExp?.field ?? '';
        this.expressionState.op1 = parsedExpL?.aritmOp ?? '';
        this.expressionState.value1 = parsedExpL?.rightExp?.constant ?? '';
        this.expressionState.op2 = parsedExpR?.aritmOp ?? '';
        this.expressionState.value2 = parsedExpR?.rightExp?.constant ?? '';
    }
}
